/**
 * Canonical client logout.
 *
 * `performLogout()` is the ONLY code path that calls `POST /api/v1/auth/logout`.
 * It always tears down local authenticated / private UI state (so private data
 * disappears the moment the member asks to log out), then reports whether the
 * server actually confirmed the logout:
 *
 *   { serverConfirmed: true }  – server responded OK; the session is ended.
 *   { serverConfirmed: false } – the request failed. Local state is still
 *                                cleared, but the server session MAY still exist
 *                                and could be recognised again on a later reload.
 *                                Callers should tell the member to retry before
 *                                relying on the session being terminated.
 *
 * It owns cleanup only — callers own any post-logout navigation / messaging.
 * The promise never rejects.
 *
 * It deliberately does NOT touch the per-member workspace storage namespace (or
 * any other durable workspace state): logging out ends *authorization to display*
 * that data, it does not delete it. After the same member logs back in, normal
 * workspace hydration resumes. This holds regardless of whether workspace state
 * later moves server-side.
 */
import axios from 'axios';
import { authState, clearAuthProfileFlags } from '@/utils/auth';
import { clearPersonalChartSession } from '@/composables/useChartStatus';
import { clearStoredDiscovery } from '@/composables/usePersonalSkyDiscovery';
import { clearCachedChartDisplaySettings } from '@/utils/chartDisplaySettings';

export const LOGOUT_UNCONFIRMED_MESSAGE =
  'We could not confirm your logout with the server. Reload and try again before relying on your session being ended.';

export async function performLogout() {
  let serverConfirmed = false;
  try {
    await axios.post('/api/v1/auth/logout');
    serverConfirmed = true;
  } catch (error) {
    console.error('Error during logout:', error);
  }

  // Local private-state teardown — runs whether or not the server confirmed.
  delete axios.defaults.headers.common['Authorization'];
  clearPersonalChartSession();
  clearStoredDiscovery();
  clearCachedChartDisplaySettings();
  authState.isAuthenticated = false;
  // Nulls authState.userId; mounted useUserMenuIdentity() instances reset their
  // own refs via their authState.isAuthenticated watcher.
  clearAuthProfileFlags();

  return { serverConfirmed };
}
